import React, { useEffect, useState } from 'react';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

// Solo valores medidos. Las respuestas que deciden el escenario (enfermedades,
// síntomas, actividad) no se corrigen acá: si estaban mal, se hace un retest.
// El nombre se cambia en la ficha del alumno, para todas sus entrevistas.
const CAMPOS = [
  { grupo: 'Datos', items: [
    { k: 'age', label: 'Edad', unidad: 'años', entero: true },
    { k: 'weight', label: 'Peso', unidad: 'kg' },
    { k: 'height', label: 'Talla', unidad: 'cm' },
    { k: 'waist_circumference', label: 'Cintura', unidad: 'cm' },
  ] },
  { grupo: 'Cardiovascular', items: [
    { k: 'resting_heart_rate', label: 'FC de reposo', unidad: 'lpm' },
    { k: 'blood_pressure_systolic', label: 'PA sistólica', unidad: 'mmHg', entero: true },
    { k: 'blood_pressure_diastolic', label: 'PA diastólica', unidad: 'mmHg', entero: true },
  ] },
  { grupo: 'Laboratorio', items: [
    { k: 'cholesterol_total', label: 'Colesterol total', unidad: 'mg/dl' },
    { k: 'hdl', label: 'HDL', unidad: 'mg/dl' },
    { k: 'ldl', label: 'LDL', unidad: 'mg/dl' },
    { k: 'triglycerides', label: 'Triglicéridos', unidad: 'mg/dl' },
    { k: 'glucose', label: 'Glucemia', unidad: 'mg/dl' },
  ] },
];
const SEXOS = ['Masculino', 'Femenino', 'No binario', 'Prefiero no responder'];

function inicial(evaluation) {
  const f = { sex: evaluation.sex || '' };
  CAMPOS.forEach((g) => g.items.forEach(({ k }) => {
    f[k] = evaluation[k] === null || evaluation[k] === undefined ? '' : String(evaluation[k]);
  }));
  return f;
}

/** Corregir valores mal anotados de una entrevista o retest. */
export default function CorregirDatosDialog({ evaluation, open, onOpenChange, onCorregida }) {
  const [form, setForm] = useState(() => inicial(evaluation));
  const [aviso, setAviso] = useState(null); // { antes, despues }
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (open) { setForm(inicial(evaluation)); setAviso(null); }
  }, [open, evaluation]);

  // Solo se manda lo que cambió y tiene un número válido.
  const cambios = () => {
    const out = {};
    CAMPOS.forEach((g) => g.items.forEach(({ k, entero }) => {
      const txt = String(form[k]).trim().replace(',', '.');
      if (txt === '') return;
      const n = entero ? parseInt(txt, 10) : parseFloat(txt);
      if (Number.isFinite(n) && n !== evaluation[k]) out[k] = n;
    }));
    if (form.sex && form.sex !== evaluation.sex) out.sex = form.sex;
    return out;
  };

  const guardar = async (confirmar = false) => {
    const datos = cambios();
    if (Object.keys(datos).length === 0) {
      toast.error('No cambiaste ningún dato.');
      return;
    }
    setGuardando(true);
    try {
      const r = await api.corregirEvaluacion(evaluation.id, { ...datos, confirmar });
      if (r?.requiere_confirmacion) {
        setAviso({ antes: r.escenario_antes, despues: r.escenario_despues });
        return;
      }
      toast.success('Datos corregidos. El informe ya sale con los valores nuevos.');
      onCorregida(r);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.message || 'No se pudo guardar la corrección');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !guardando && onOpenChange(o)}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="corregir-datos-dialog">
        <DialogHeader>
          <DialogTitle>Corregir datos</DialogTitle>
          <DialogDescription>
            Para valores mal anotados. Se recalcula todo y la corrección queda registrada con la fecha.
            Si una respuesta de la entrevista estaba mal (enfermedades, síntomas, actividad), hacé un
            retest. El nombre se cambia en la ficha del alumno.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-1">
          {CAMPOS.map((g) => (
            <div key={g.grupo}>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{g.grupo}</p>
              <div className="grid grid-cols-2 gap-3">
                {g.items.map(({ k, label, unidad }) => (
                  <div key={k} className="space-y-1">
                    <Label htmlFor={`corr-${k}`} className="text-sm">{label} <span className="text-slate-400">({unidad})</span></Label>
                    <Input
                      id={`corr-${k}`}
                      inputMode="decimal"
                      value={form[k]}
                      onChange={(e) => { setForm({ ...form, [k]: e.target.value }); setAviso(null); }}
                    />
                  </div>
                ))}
                {g.grupo === 'Datos' && (
                  <div className="space-y-1">
                    <Label htmlFor="corr-sex" className="text-sm">Sexo</Label>
                    <select
                      id="corr-sex"
                      value={form.sex}
                      onChange={(e) => { setForm({ ...form, sex: e.target.value }); setAviso(null); }}
                      className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                    >
                      {SEXOS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                )}
              </div>
            </div>
          ))}

          {aviso && (
            <div className="flex gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="corregir-aviso-escenario">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <p>
                Con estos datos el escenario pasa de <strong>{aviso.antes}</strong> a{' '}
                <strong>{aviso.despues}</strong>. Si es correcto, confirmá y se guarda.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={guardando}>Cancelar</Button>
          <Button onClick={() => guardar(Boolean(aviso))} disabled={guardando} data-testid="corregir-guardar">
            {guardando ? <Loader2 className="h-4 w-4 animate-spin" /> : aviso ? 'Confirmar y guardar' : 'Guardar corrección'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
